import { Close, DeleteFive, Plus, SmilingFace, Star, Tag, Edit } from '@icon-park/react'

import { EditablePolarityValues, PolarityID, PolarityValue, SearchResponse, UpdateMentionRequest } from '@shared/models'

import { Tokens } from '@/assets/tokens'

import { useRouting, useInput } from '@/hooks'
import { Alert, Api, Confirmation } from '@/modules'
import { CommandMethods } from '@/modules/api/useCommand'

import { Input } from '@/components'

import { Str, truncateString, sanitizePublicationHtml } from '@/utils/strings'

import { Button, Flex, SelectionOption, Selector, TableImage, Switch, Icon } from '@/components'

import { useMentionsContext } from '../../Mentions.context'
import { appendAtIfNeeded } from '../../utils'

import { useObserver } from '@/modules/observer'
import { MentionResponse } from '@shared/models'
import { useEffect, useState } from 'react'
import { AddKeyMessageModal } from './AddKeyMessageModal.component'
import { AddThemeModal } from './AddThemeModal.component'
import {
  Container,
  KeyMessageDeleteButton,
  KeyMessageTag,
  KeyMessageText,
  Overlay,
  SpokespersonTag,
} from './EditModal.styles'
import { AddSpokespersonModal } from './AddSpokespersonModal.component'

interface ThemeTag {
  themeInternalID: string
  themeName: string
  themeGroupInternalID: string
  themeGroupName: string
  isAddition: boolean
  tempId?: string
}

interface KeyMessageTag {
  internalID: string
  name: string
  isAddition: boolean
  tempId?: string
}

interface SpokespersonTag {
  internalID: string
  name: string
  isAddition: boolean
  tempId?: string
}

export function EditModal(): JSX.Element | null {
  const { companyID } = useRouting()
  const {
    currentMention: currMention,
    polarities,
    isSearchingRoles,
    roles,
    searchRoles,
    selectMention,
    applyFilters,
    loadKeyMessagesForMentions,
  } = useMentionsContext()
  const currentMention = currMention?.val

  const { sendMessage } = useObserver()

  const [themeTags, setThemeTags] = useState<ThemeTag[]>([])
  const [keyMessageTags, setKeyMessageTags] = useState<KeyMessageTag[]>([])
  const [spokespersonTags, setSpokespersonTags] = useState<SpokespersonTag[]>([])
  const [showAddThemeModal, setShowAddThemeModal] = useState(false)
  const [showAddKeyMessageModal, setShowAddKeyMessageModal] = useState(false)
  const [showAddSpokespersonModal, setShowAddSpokespersonModal] = useState(false)
  const [approaches, setApproaches] = useState<SearchResponse[]>([])
  const { binder: bindPublisher, reset: resetPublisher } = useInput()
  const showEditPublisherBinder = Switch.Hook()
  const { binder: bindPolarity, selectedOption: polarity, reset: resetPolarity } = Selector.Binder()
  const { binder: bindRole, selectedOption: role, reset: resetRole } = Selector.Binder()
  const { binder: bindApproach, selectedOption: approach, reset: resetApproach } = Selector.Binder()

  const { sendAlert } = Alert.Hook()

  const [searchApproachesAPI, isSearchingApproaches] = Api.PromiseQuery<SearchResponse[]>(`approaches/search`)

  const { open: openConfirmation } = Confirmation.Hook({
    icon: DeleteFive,
    text: 'Esta ação não tem como ser desfeita. Todos os valores associados à essa menção serão perdidos',
    confirmText: 'Sim! Apagar menção',
  })

  const { isRunning: isUpdatingMention, run: updateMention } = Api.Command<UpdateMentionRequest>(
    CommandMethods.PATCH,
    `companies/:companyID/mentions/:mentionID`,
  )
  const { isRunning: isDeletingMention, run: deleteMention } = Api.Command<void>(
    CommandMethods.DELETE,
    `companies/:companyID/mentions/:mentionID`,
  )

  useEffect(() => {
    loadApproaches()
  }, [])

  const loadApproaches = async () => {
    try {
      const [data] = await searchApproachesAPI({
        name: '',
        size: 10000,
        orderBy: 'name:ASC',
      })
      if (data) {
        setApproaches(data)
      }
    } catch (error) {
      sendAlert({
        type: 'error',
        title: 'Erro ao carregar abordagens',
        message: 'Não foi possível carregar as abordagens.',
      })
    }
  }

  useEffect(() => {
    if (!currentMention || !approaches?.length) {
      resetApproach(undefined)
      return
    }

    let approachToSet: SelectionOption | undefined = undefined

    let foundApproachOption: SearchResponse | undefined
    if (currentMention.approachInternalID) {
      foundApproachOption = approaches.find((a) => a.value === currentMention.approachInternalID)
    }
    if (!foundApproachOption && currentMention.approach) {
      foundApproachOption = approaches.find((a) => a.text.toUpperCase() === currentMention.approach.toUpperCase())
    }

    if (foundApproachOption) {
      approachToSet = {
        text: Str.from(foundApproachOption.text).initCap,
        value: foundApproachOption.value,
      }
    }
    resetApproach(approachToSet)
  }, [currentMention, approaches])

  useEffect(() => {
    if (!currentMention) {
      showEditPublisherBinder.reset()
      resetPublisher(undefined)
      resetPolarity(undefined)
      resetRole(undefined)
      resetApproach(undefined)
      setKeyMessageTags([])
    }
  }, [currentMention?.postID])

  useEffect(() => {
    if (currentMention?.postID) {
      loadKeyMessagesForMentions([currentMention.postID], currentMention.brandID)
    }
  }, [currentMention?.postID, loadKeyMessagesForMentions])

  useEffect(() => {
    if (!currentMention) {
      setKeyMessageTags([])
      return
    }

    if (currentMention.keyMessages && currentMention.keyMessages.length > 0) {
      const tags: KeyMessageTag[] = currentMention.keyMessages.map((km) => ({
        internalID: km.internalID,
        name: km.name,
        isAddition: false,
      }))
      setKeyMessageTags(tags)
    } else {
      setKeyMessageTags([])
    }
  }, [currentMention?.postID])

  useEffect(() => {
    if (!currentMention) {
      setSpokespersonTags([])
      return
    }

    if (currentMention.spokespersons && currentMention.spokespersons.length > 0) {
      const tags: SpokespersonTag[] = currentMention.spokespersons.map((sp) => ({
        internalID: sp.internalID,
        name: sp.name,
        isAddition: false,
      }))
      setSpokespersonTags(tags)
    } else {
      setSpokespersonTags([])
    }
  }, [currentMention?.postID])

  useEffect(() => {
    if (!currentMention) {
      setThemeTags([])
      return
    }

    if (currentMention.themeGroup && currentMention.brandID) {
      const tags: ThemeTag[] = []

      currentMention.themeGroup.forEach((group) => {
        group.themes.forEach((theme) => {
          tags.push({
            themeInternalID: theme.internalID,
            themeName: theme.name,
            themeGroupInternalID: group.internalID,
            themeGroupName: group.name,
            isAddition: false,
          })
        })
      })

      setThemeTags(tags)
    } else {
      setThemeTags([])
    }
  }, [currentMention?.postID])

  useEffect(() => {
    if (!currentMention || !polarities) {
      resetPolarity(undefined)
      return
    }

    let polarityToSet: SelectionOption | undefined = undefined
    if (currentMention.polarity) {
      const currentPolarityValue = currentMention.polarity.toUpperCase() as PolarityValue
      const polarityId = PolarityID.get(currentPolarityValue)
      const foundPolarityOption = polarities.find((p) => PolarityID.get(p.value as PolarityValue) === polarityId)

      if (foundPolarityOption) {
        polarityToSet = {
          text: Str.from(foundPolarityOption.text).initCap,
          value: PolarityID.get(foundPolarityOption.value as PolarityValue) ?? 0,
        }
      }
    }
    resetPolarity(polarityToSet)
  }, [currentMention, polarities])

  useEffect(() => {
    if (!currentMention || !roles?.length) {
      resetRole(undefined)
      return
    }

    let roleToSet: SelectionOption | undefined = undefined

    let foundRoleOption: SearchResponse | undefined
    if (currentMention.role) {
      foundRoleOption = roles.find((r) => r.text.toUpperCase() === currentMention.role.toUpperCase())
    }

    if (foundRoleOption) {
      roleToSet = {
        text: Str.from(foundRoleOption.text).initCap,
        value: foundRoleOption.value,
      }
    }
    resetRole(roleToSet)
  }, [currentMention, roles])

  useEffect(() => {
    if (!currentMention) {
      resetPublisher(undefined)
      return
    }
    resetPublisher(appendAtIfNeeded(currentMention.profile))
  }, [currentMention])

  function onConfirmationAccepted(mentionID: string): void {
    deleteMention<void>(
      {
        binds: [
          ['companyID', currentMention!.companyID],
          ['mentionID', mentionID],
        ],
        onSuccess: () => {
          sendAlert({
            type: 'success',
            title: 'Exclusão da menção',
            message: 'Menção excluída com sucesso',
          })
          selectMention()
          applyFilters()
        },
      },
      { params: { brandID: currentMention!.brandID } },
    )
  }

  function onSuccess(): void {
    if (currentMention) {
      const newMention: MentionResponse = {
        ...currentMention,
        publisher: bindPublisher.value,
        polarity: (polarity?.text || currentMention.polarity).toString().toUpperCase(),
        role: (role?.text || currentMention.role).toString().toUpperCase(),
        approach: (approach?.text || currentMention.approach).toString().toUpperCase(),
      }
      sendMessage('mention_updated', { idx: currMention.idx, mention: newMention })
    }
    applyFilters()
    selectMention(undefined)
  }

  function onFail(error: Error): void {
    sendAlert({
      type: 'error',
      title: 'Erro ao atualizar menção',
      message: `Ocorreu um erro ao atualizar a menção: ${error.message}`,
    })
  }

  const handleAddTheme = (
    themeInternalID: string,
    themeName: string,
    themeGroupInternalID: string,
    themeGroupName: string,
  ) => {
    const newTag: ThemeTag = {
      themeInternalID,
      themeName,
      themeGroupInternalID,
      themeGroupName,
      isAddition: true,
      tempId: `temp_${Date.now()}_${Math.random()}`,
    }

    setThemeTags((prev) => [...prev, newTag])
    setShowAddThemeModal(false)
  }

  const handleAddKeyMessage = (internalID: string, name: string) => {
    const newTag: KeyMessageTag = {
      internalID,
      name,
      isAddition: true,
      tempId: `temp_${Date.now()}_${Math.random()}`,
    }

    setKeyMessageTags((prev) => [...prev, newTag])
    setShowAddKeyMessageModal(false)
  }

  const handleAddSpokesperson = (internalID: string, name: string) => {
    const newTag: SpokespersonTag = {
      internalID,
      name,
      isAddition: true,
      tempId: `temp_${Date.now()}_${Math.random()}`,
    }

    setSpokespersonTags((prev) => [...prev, newTag])
    setShowAddSpokespersonModal(false)
  }

  const handleRemoveTheme = (tag: ThemeTag) => {
    if (tag.isAddition) {
      setThemeTags((prev) => prev.filter((t) => t.tempId !== tag.tempId))
    } else {
      setThemeTags((prev) => prev.filter((t) => t.themeInternalID !== tag.themeInternalID))
    }
  }

  const handleRemoveKeyMessage = (tag: KeyMessageTag) => {
    if (tag.isAddition) {
      setKeyMessageTags((prev) => prev.filter((t) => t.tempId !== tag.tempId))
    } else {
      setKeyMessageTags((prev) => prev.filter((t) => t.internalID !== tag.internalID))
    }
  }

  const handleRemoveSpokesperson = (tag: SpokespersonTag) => {
    if (tag.isAddition) {
      setSpokespersonTags((prev) => prev.filter((t) => t.tempId !== tag.tempId))
    } else {
      setSpokespersonTags((prev) => prev.filter((t) => t.internalID !== tag.internalID))
    }
  }

  function handleUpdate(): void {
    const input = {
      brandID: currentMention!.brandID,
    } as UpdateMentionRequest

    const originalPublisher = appendAtIfNeeded(currentMention!.profile)
    const newPublisher = bindPublisher.value.trim()

    if (newPublisher !== originalPublisher) {
      input.publisher = newPublisher
    }

    if (polarity) input.polarityInternalID = polarity.value as number
    if (role) input.roleInternalID = role.value as number
    if (approach) input.approachInternalID = approach.value as number

    const originalThemeIds = new Set(
      currentMention!.themeGroup?.flatMap((group) => group.themes.map((theme) => theme.internalID)) || [],
    )
    const currentThemeIds = new Set(themeTags.map((tag) => tag.themeInternalID))

    const themeAdditions = themeTags
      .filter((tag) => tag.isAddition || !originalThemeIds.has(tag.themeInternalID))
      .map((tag) => ({
        themeGroupInternalID: tag.themeGroupInternalID,
        themeInternalID: tag.themeInternalID,
      }))

    const themeDeletions = Array.from(originalThemeIds)
      .filter((themeId) => !currentThemeIds.has(themeId))
      .map((themeId) => ({
        themeInternalID: themeId,
      }))

    if (themeAdditions.length > 0) {
      input.themeAdditions = themeAdditions
    }

    if (themeDeletions.length > 0) {
      input.themeDeletions = themeDeletions
    }

    const originalKeyMessagesIds = new Set(currentMention!.keyMessages?.flatMap((km) => km.internalID) || [])
    const currentKeyMessagesIds = new Set(keyMessageTags.map((tag) => tag.internalID))

    const keyMessageAdditions = keyMessageTags
      .filter((tag) => tag.isAddition || !originalKeyMessagesIds.has(tag.internalID))
      .map((tag) => ({
        keyMessageInternalID: tag.internalID,
      }))

    const keyMessageDeletions = Array.from(originalKeyMessagesIds)
      .filter((keyMessageID) => !currentKeyMessagesIds.has(keyMessageID))
      .map((keyMessageID) => ({
        keyMessageInternalID: keyMessageID,
      }))

    if (keyMessageAdditions.length > 0) {
      input.keyMessageAdditions = keyMessageAdditions
    }

    if (keyMessageDeletions.length > 0) {
      input.keyMessageDeletions = keyMessageDeletions
    }

    const originalspokespersonsIds = new Set(currentMention!.spokespersons?.flatMap((km) => km.internalID) || [])
    const currentspokespersonsIds = new Set(spokespersonTags.map((tag) => tag.internalID))

    const spokespersonAdditions = spokespersonTags
      .filter((tag) => tag.isAddition || !originalspokespersonsIds.has(tag.internalID))
      .map((tag) => ({
        spokespersonInternalID: tag.internalID,
      }))

    const spokespersonDeletions = Array.from(originalspokespersonsIds)
      .filter((spokespersonID) => !currentspokespersonsIds.has(spokespersonID))
      .map((spokespersonID) => ({
        spokespersonInternalID: spokespersonID,
      }))

    if (spokespersonAdditions.length > 0) {
      input.spokespersonAdditions = spokespersonAdditions
    }

    if (spokespersonDeletions.length > 0) {
      input.spokespersonDeletions = spokespersonDeletions
    }

    const publisherChanged = newPublisher !== originalPublisher
    const hasUpdates =
      publisherChanged ||
      polarity ||
      role ||
      approach ||
      themeAdditions.length > 0 ||
      themeDeletions.length > 0 ||
      keyMessageAdditions.length > 0 ||
      keyMessageDeletions.length > 0 ||
      spokespersonAdditions.length > 0 ||
      spokespersonDeletions.length > 0

    if (!hasUpdates) {
      sendAlert({
        type: 'error',
        title: 'Nenhuma alteração',
        message: 'Nenhuma modificação foi realizada.',
      })
      return
    }

    updateMention({
      input,
      binds: [
        ['companyID', currentMention!.companyID],
        ['mentionID', currentMention!.postID],
      ],
      onSuccess: async () => {
        onSuccess()
        await applyFilters()
      },
      onFail,
    })
  }

  if (!companyID || !currentMention) return null

  return (
    <>
      <Overlay>
        <Container>
          <Flex $type="row" $justify="center" $align="center" $fit style={{ position: 'relative' }}>
            <h3 style={{ margin: 0, textAlign: 'center', flex: 1 }}>Atualização da Anotação</h3>
            <Button.Root
              $variant="link"
              onClick={() => selectMention(undefined)}
              style={{ position: 'absolute', right: 0 }}>
              <Button.Icon value={Close} color={Tokens.ColorTextPrimary} />
            </Button.Root>
          </Flex>

          <Flex $type="row" $justify="flex-start" $align="flex-start" $fit $gap={30}>
            <Flex $type="column" $justify="center" $align="flex-start" $gap={10}>
              <TableImage id={currentMention.channelLogoID} hyperlink={currentMention.hyperlink} />
              <TableImage id={currentMention.brandLogo} />
            </Flex>
            <Flex $type="column" $justify="center" $align="stretch" $fit style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                <div onClick={() => showEditPublisherBinder.toggle()} title="Editar Publicador">
                  <Icon size={18} value={Edit} color={Tokens.ColorUIWarnDark} />
                </div>
                <h4
                  style={{
                    display: showEditPublisherBinder.isChecked ? 'none' : 'flex',
                    height: '3.25rem',
                    alignItems: 'center',
                    paddingLeft: '16px',
                  }}>
                  {bindPublisher.value}
                </h4>
                <div style={{ display: showEditPublisherBinder.isChecked ? 'block' : 'none' }}>
                  <Input {...bindPublisher} />
                </div>
              </div>
              <span
                dangerouslySetInnerHTML={{
                  __html: truncateString(sanitizePublicationHtml(currentMention.publication), 800),
                }}
              />
            </Flex>
          </Flex>

          <Flex $type="row" $justify="flex-start" $align="center" $fit $gap={16} style={{ paddingLeft: '75px' }}>
            <Selector.Default
              {...bindPolarity}
              label="Favorabilidade"
              labelStyle={{ fontSize: '16px', fontWeight: '600', color: Tokens.ColorTextPrimary }}
              placeholder="Polaridade"
              options={
                polarities
                  ?.filter((item) => EditablePolarityValues.includes(item.value as PolarityValue))
                  .map((item) => ({
                    text: Str.from(item.text).initCap,
                    value: PolarityID.get(item.value as PolarityValue) ?? 0,
                  })) ?? []
              }>
              <Selector.Icon value={SmilingFace} color={Tokens.ColorUIPrimary} />
            </Selector.Default>

            <Selector.Searchable
              {...bindRole}
              label="Abordagem"
              labelStyle={{ fontSize: '16px', fontWeight: '600', color: Tokens.ColorTextPrimary }}
              placeholder="Papel"
              options={roles?.map((item) => ({ text: Str.from(item.text).initCap, value: item.value })) ?? []}
              onSearch={(searchParam: string): void => {
                searchRoles({ name: searchParam })
              }}
              loading={isSearchingRoles}>
              <Selector.Icon value={Star} color={Tokens.ColorUIPrimary} />
            </Selector.Searchable>

            <Selector.Default
              {...bindApproach}
              label="Formato"
              labelStyle={{ fontSize: '16px', fontWeight: '600', color: Tokens.ColorTextPrimary }}
              placeholder="Abordagem"
              options={approaches?.map((item) => ({ text: Str.from(item.text).initCap, value: item.value })) ?? []}
              loading={isSearchingApproaches}>
              <Selector.Icon value={Tag} color={Tokens.ColorUIPrimary} />
            </Selector.Default>
          </Flex>

          <Flex
            $type="column"
            $justify="flex-start"
            $align="stretch"
            $fit
            style={{ paddingLeft: '75px', paddingTop: '32px' }}>
            <Flex $type="row" $justify="space-between" $align="center" $fit style={{ marginBottom: '8px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: Tokens.ColorTextPrimary, margin: 0 }}>
                Porta-vozes
              </h4>
              <Button.Root
                onClick={() => setShowAddSpokespersonModal(true)}
                $variant="secondary"
                style={{ height: '32px' }}>
                <Button.Icon value={Plus} color={Tokens.ColorUIPrimary} />
                <Button.Text value="Adicionar porta-voz" />
              </Button.Root>
            </Flex>
            <Flex $type="column" $align="stretch" $gap={8} $fit>
              <Flex $type="row" $align="center" $gap={8} style={{ flexWrap: 'wrap' }}>
                {spokespersonTags.map((tag) => (
                  <SpokespersonTag key={tag.internalID}>
                    <KeyMessageText> 👤 {tag.name}</KeyMessageText>
                    <KeyMessageDeleteButton
                      onClick={() => handleRemoveSpokesperson(tag)}
                      title="Remover mensagem-chave">
                      ×
                    </KeyMessageDeleteButton>
                  </SpokespersonTag>
                ))}
              </Flex>
            </Flex>
          </Flex>

          <Flex
            $type="column"
            $justify="flex-start"
            $align="stretch"
            $fit
            style={{ paddingLeft: '75px', paddingTop: '32px' }}>
            <Flex $type="row" $justify="space-between" $align="center" $fit style={{ marginBottom: '8px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: Tokens.ColorTextPrimary, margin: 0 }}>
                Mensagens-Chave
              </h4>
              <Button.Root
                onClick={() => setShowAddKeyMessageModal(true)}
                $variant="secondary"
                style={{ height: '32px' }}>
                <Button.Icon value={Plus} color={Tokens.ColorUIPrimary} />
                <Button.Text value="Adicionar Mensagem-Chave" />
              </Button.Root>
            </Flex>
            <Flex $type="column" $align="stretch" $gap={8} $fit>
              <Flex $type="row" $align="center" $gap={8} style={{ flexWrap: 'wrap' }}>
                {keyMessageTags.map((tag) => (
                  <KeyMessageTag key={tag.internalID}>
                    <KeyMessageText>{tag.name}</KeyMessageText>
                    <KeyMessageDeleteButton onClick={() => handleRemoveKeyMessage(tag)} title="Remover mensagem-chave">
                      ×
                    </KeyMessageDeleteButton>
                  </KeyMessageTag>
                ))}
              </Flex>
            </Flex>
          </Flex>

          <Flex
            $type="column"
            $justify="flex-start"
            $align="stretch"
            $fit
            style={{ paddingLeft: '75px', paddingTop: '24px' }}>
            <Flex $type="row" $justify="space-between" $align="center" $fit style={{ marginBottom: '12px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: Tokens.ColorTextPrimary, margin: 0 }}>
                Temas Associados ({themeTags.length})
              </h4>
              <Button.Root onClick={() => setShowAddThemeModal(true)} $variant="secondary" style={{ height: '32px' }}>
                <Button.Icon value={Plus} color={Tokens.ColorUIPrimary} />
                <Button.Text value="Adicionar Tema" />
              </Button.Root>
            </Flex>

            <Flex $type="column" $align="flex-start" $gap={12} $fit>
              {Object.entries(
                themeTags.reduce(
                  (acc, tag) => {
                    if (!acc[tag.themeGroupInternalID]) {
                      acc[tag.themeGroupInternalID] = {
                        groupName: tag.themeGroupName,
                        themes: [],
                      }
                    }
                    acc[tag.themeGroupInternalID].themes.push(tag)
                    return acc
                  },
                  {} as Record<string, { groupName: string; themes: ThemeTag[] }>,
                ),
              ).map(([groupId, groupData]) => (
                <Flex key={groupId} $type="column" $align="flex-start" $gap={6} $fit>
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: Tokens.ColorTextSecondary,
                    }}>
                    {groupData.groupName}:
                  </span>
                  <Flex $type="row" $align="center" $gap={8} style={{ flexWrap: 'wrap' }}>
                    {groupData.themes.map((tag) => (
                      <span
                        key={tag.tempId || `${tag.themeGroupInternalID}-${tag.themeInternalID}`}
                        style={{
                          backgroundColor: Tokens.ColorUIWarnLight,
                          color: Tokens.ColorTextPrimary,
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}>
                        <span title={tag.themeName}>{tag.themeName}</span>
                        <button
                          onClick={() => handleRemoveTheme(tag)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            color: Tokens.ColorUIDangerDim,
                            fontSize: '14px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          title="Remover tema">
                          ×
                        </button>
                      </span>
                    ))}
                  </Flex>
                </Flex>
              ))}
            </Flex>

            {themeTags.length === 0 && (
              <div
                style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: Tokens.ColorTextTertiary,
                  backgroundColor: Tokens.ColorBackground,
                  borderRadius: '8px',
                }}>
                Nenhum tema associado. Clique em "Adicionar Tema" para começar.
              </div>
            )}
          </Flex>

          <Flex $type="row" $justify="space-between" $align="center" $fit style={{ marginTop: '32px' }}>
            <Button.Root
              onClick={() => openConfirmation(() => onConfirmationAccepted(currentMention.postID))}
              $variant="danger"
              $loading={isDeletingMention}>
              <Button.Icon value={DeleteFive} color={Tokens.ColorWhite} />
              <Button.Text value="Excluir Menção" />
            </Button.Root>

            <Flex $type="row" $justify="flex-end" $align="center" $fit $gap={16}>
              <Button.Root $variant="secondary" onClick={() => selectMention(undefined)} style={{ width: '7rem' }}>
                <Button.Text value="Cancelar" />
              </Button.Root>
              <Button.Root
                $variant="primary"
                onClick={handleUpdate}
                $loading={isUpdatingMention}
                style={{ width: '7rem' }}>
                <Button.Text value="Atualizar" />
              </Button.Root>
            </Flex>
          </Flex>
        </Container>
      </Overlay>

      {showAddThemeModal && (
        <AddThemeModal
          companyID={currentMention.companyID}
          brandID={currentMention.brandID}
          currentMentionThemes={themeTags.filter((t) => !t.isAddition).map((t) => t.themeInternalID)}
          alreadyAddedThemes={themeTags.filter((t) => t.isAddition).map((t) => t.themeInternalID)}
          onCancel={() => setShowAddThemeModal(false)}
          onAdd={handleAddTheme}
        />
      )}

      {showAddKeyMessageModal && (
        <AddKeyMessageModal
          sourceInternalID={currentMention.sourceInternalID}
          companyID={currentMention.companyID}
          brandID={currentMention.brandID}
          currentKeyMessages={keyMessageTags.map((tag) => tag.internalID)}
          onCancel={() => setShowAddKeyMessageModal(false)}
          onAdd={handleAddKeyMessage}
        />
      )}

      {showAddSpokespersonModal && (
        <AddSpokespersonModal
          companyID={currentMention.companyID}
          brandID={currentMention.brandID}
          currentSpokespersons={spokespersonTags.map((tag) => tag.internalID)}
          onCancel={() => setShowAddSpokespersonModal(false)}
          onAdd={handleAddSpokesperson}
        />
      )}
    </>
  )
}
